import React, { useEffect, useState } from "react";
import PowerUpToken from "../../shared/PowerUpToken";
import { POWERUPS, type PowerUpColor } from "../../shared/powerups";
import { fetchPowerUpValues, savePowerUpValues, type PowerUpValues } from "../../shared/admin-bots";
import styles from "./BotLeaderboardPage.module.scss";

// Bot management, Power-ups tab: what every bot's Power-up greed chases. One
// global value per power-up, 0 (ignored outright, even at greed 1) to 1 (full
// pull). A bot's own greed says how much it cares about power-ups; this says
// which ones are worth caring about. The values are simply the values: there
// is no "default" to compare against or go back to on this page (core's
// ai/powerup-values.js only holds what they start as, and what offline play
// uses). What is saved here rides on every match from its start.

const GROUPS: { color: PowerUpColor; title: string; about: string }[] = [
  { color: "green", title: "Green", about: "helps the one who takes it" },
  { color: "red", title: "Red", about: "hurts everyone else" },
  { color: "blue", title: "Blue", about: "hits everybody, the taker included" },
];

export default function PowerUpValuesTab() {
  const [data, setData] = useState<PowerUpValues | { error: string } | null>(null);
  const [draft, setDraft] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    fetchPowerUpValues().then((res) => {
      if (!live) return;
      setData(res);
      if (!("error" in res)) setDraft({ ...res.values });
    });
    return () => { live = false; };
  }, []);

  if (!data) return <p className={styles.state}>Loading the power-up values…</p>;
  if ("error" in data) {
    return (
      <p className={styles.state}>
        {data.error === "not_signed_in" ? "Sign in with an admin account to see and tune the power-up values."
          : data.error === "not_admin" ? "This account isn't on the admin list."
          : `Couldn't load the power-up values (${data.error}).`}
      </p>
    );
  }

  const { values } = data;
  const dirty = POWERUPS.some((p) => (draft[p.key] ?? 0) !== (values[p.key] ?? 0));

  async function save(next: Record<string, number>) {
    setBusy(true);
    setMsg(null);
    const res = await savePowerUpValues(next);
    setBusy(false);
    if ("error" in res) {
      setMsg({ ok: false, text: `Couldn't save (${res.error}).` });
      return;
    }
    setData(res);
    setDraft({ ...res.values });
    setMsg({ ok: true, text: "Saved. Every match that starts from now on uses these values." });
  }

  return (
    <div className={styles.puTab}>
      <section className={styles.levelCard}>
        <div className={styles.levelHead}>
          <h2>Power-up values</h2>
          <span className={styles.levelFeeds}>What Power-up greed chases. The same for every bot.</span>
        </div>
        <p className={styles.levelNote}>
          <strong>0</strong> = the bots ignore it completely, even at greed 1. <strong>1</strong> = full pull: with greed 1 it works as
          greed always did. In between scales the pull. A bot still weighs who the effect hits and whether a rival gets there first.
        </p>
      </section>

      <div className={styles.puGroups}>
        {GROUPS.map((g) => (
          <section key={g.color} className={styles.puGroup}>
            <h3><span className={styles["pu" + g.title]}>{g.title}</span> {g.about}</h3>
            {/* most valued first. Ordered by the SAVED values, not the sliders as they
                move: a row that jumped away mid-drag would be unusable. They re-sort on save. */}
            {POWERUPS.filter((p) => p.color === g.color)
              .map((p, i) => ({ p, i }))
              .sort((a, b) => (values[b.p.key] ?? 0) - (values[a.p.key] ?? 0) || a.i - b.i)
              .map(({ p }) => {
              const v = draft[p.key] ?? 0;
              const changed = v !== (values[p.key] ?? 0);
              return (
                <div key={p.key} className={[styles.puRow, changed ? styles.settingDirty : "", v === 0 ? styles.puIgnored : ""].filter(Boolean).join(" ")}>
                  <PowerUpToken icon={p.icon} color={p.color} size={34} />
                  <span className={styles.puName}>{p.name}</span>
                  <input className={styles.settingSlider} type="range" min={0} max={1} step={0.05} value={v} aria-label={p.name} onChange={(e) => setDraft((d) => ({ ...d, [p.key]: Number(e.target.value) }))} />
                  <span className={styles.settingValue}>{v === 0 ? "ignored" : v.toFixed(2)}</span>
                  {/* an edited row says what it was, and one click puts it back */}
                  <span className={styles.settingDefault}>
                    {changed && <button type="button" onClick={() => setDraft((d) => ({ ...d, [p.key]: values[p.key] ?? 0 }))} title="Undo this one">was {values[p.key] ?? 0} ↺</button>}
                  </span>
                </div>
              );
            })}
          </section>
        ))}
      </div>

      <div className={styles.editorFoot}>
        <div className={styles.editorButtons}>
          <button type="button" className="app-btn app-btn-primary app-btn-sm" disabled={!dirty || busy} onClick={() => save(draft)}>{busy ? "Saving…" : "Save values"}</button>
          <button type="button" className="app-btn app-btn-ghost app-btn-sm" disabled={!dirty || busy} onClick={() => setDraft({ ...values })}>Undo edits</button>
        </div>
        {msg && <p className={msg.ok ? styles.msgOk : styles.msgBad}>{msg.text}</p>}
      </div>
    </div>
  );
}
